import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPaymentHistory, downloadInvoice } from '../../api/BackendApi.jsx';
import { Loader, AlertTriangle, History, ChevronLeft, TrendingUp, TrendingDown, ChevronRight, ArrowRight, Download } from 'lucide-react';

const PaymentHistoryPage = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error'
  const [error, setError] = useState('');
  const hasFetched = useRef(false);
  
  // States for Tabs and Pagination
  const [activeTab, setActiveTab] = useState('platby'); // 'platby' | 'pouziti'
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  const operationLabels = {
    'BUY_CREDIT': 'Dobití kreditu',
    'ADD_CREDITS': 'Dobití kreditu',
    'ANALYSIS_FEE': 'Poplatek za analýzu',
    'CREDIT_REFUND': 'Vrácení poplatku za nedokončenou analýzu',
    'default': 'Jiná operace'
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchData = async () => {
      try {
        const response = await getPaymentHistory();
        setPayments(response.payments || []);
        setStatus('success');
      } catch (err) {
        console.error("Failed to fetch payment history:", err);
        setError(err.message || 'Nepodařilo se načíst historii plateb.');
        setStatus('error');
      }
    };
    fetchData();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('cs-CZ', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const AmountDisplay = ({ amount }) => {
    const value = parseFloat(amount);
    const isPositive = value >= 0;
    
    // Set color based on value
    const colorClass = isPositive ? 'text-green-600' : 'text-red-600';
    // Add '+' sign if positive
    const prefix = isPositive ? '+' : '';

    return (
      <span className={`font-semibold ${colorClass}`}>
        {isPositive ? 
          <TrendingUp className="w-4 h-4 mr-1 inline" /> : 
          <TrendingDown className="w-4 h-4 mr-1 inline" />
        }
        {prefix}{value.toFixed(2)}
      </span>
    );
  };

  const PaymentStatusBadge = ({ status }) => {
    const isPaid = status === 'paid';
    return (
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
          isPaid
            ? 'bg-green-100 text-green-700'
            : 'bg-yellow-100 text-yellow-700'
        }`}
      >
        {isPaid ? 'Zaplaceno' : 'Nezaplaceno'}
      </span>
    );
  };

  const renderContent = () => {
    if (status === 'loading') {
      return (
        <div className="flex justify-center items-center h-64">
          <Loader className="w-12 h-12 animate-spin text-blue-600" />
        </div>
      );
    }

    if (status === 'error') {
      return (
        <div className="flex flex-col items-center justify-center h-64 bg-red-50 text-red-700 p-6 rounded-lg">
          <AlertTriangle className="w-12 h-12 mb-4" />
          <h3 className="text-xl font-semibold">Chyba při načítání</h3>
          <p>{error}</p>
        </div>
      );
    }

    // Tab Filtering Logic
    const filteredPayments = payments.filter(payment => {
      if (activeTab === 'platby') {
        return payment.operation === 'BUY_CREDIT' || payment.operation === 'ADD_CREDITS';
      } else {
        return payment.operation !== 'BUY_CREDIT' && payment.operation !== 'ADD_CREDITS';
      }
    });

    // Pagination Logic based on filtered data
    const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
    const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
    const currentPayments = filteredPayments.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredPayments.length / ITEMS_PER_PAGE);
    
    const handlePageChange = (newPage) => {
      setCurrentPage(newPage);
    };

    return (
      <div>
        {/* Tabs */}
        <div className="flex space-x-8 mb-6 border-b border-gray-200">
          <button
            onClick={() => { setActiveTab('platby'); setCurrentPage(1); }}
            className={`pb-4 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'platby'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Platby a doklady
          </button>
          <button
            onClick={() => { setActiveTab('pouziti'); setCurrentPage(1); }}
            className={`pb-4 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'pouziti'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Použití kreditů
          </button>
        </div>

        {/* Empty state or Table */}
        {filteredPayments.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center p-10 bg-white rounded-2xl shadow-lg border border-gray-100">
            <History className="w-16 h-16 text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700">
              {activeTab === 'platby' ? 'Zatím nebyly provedeny žádné platby.' : 'Zatím nebyly použity žádné kredity.'}
            </h3>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <ul className="divide-y divide-gray-200">
              {/* Table Header */}
              <li className="hidden md:flex items-center justify-between p-4 bg-gray-50">
                <span className="w-1/4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Datum</span>
                <span className="w-1/4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Operace</span>
                <span className="w-1/4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Částka</span>

                {activeTab === 'platby' && (
                  <span className="w-1/5 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    STAV
                  </span>
                )}

                <span className="w-1/4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {activeTab === 'platby' ? 'STÁHNOUT DAŇOVÝ DOKLAD' : 'PŘEJÍT K ANALÝZE'}
                </span>
              </li>
              
              {/* Table Rows */}
              {currentPayments.map((payment, index) => (
                <li key={index} className="flex flex-col md:flex-row md:items-center justify-between p-4 hover:bg-gray-50 transition-colors">
                  <div className="md:w-1/4 mb-2 md:mb-0">
                    <span className="md:hidden text-xs font-bold text-gray-500">Datum: </span>
                    <span className="text-sm text-gray-700">{formatDate(payment.created_at)}</span>
                  </div>
                  
                  <div className="md:w-1/4 mb-2 md:mb-0">
                    <span className="md:hidden text-xs font-bold text-gray-500">Operace: </span>
                    <span className="text-sm font-medium text-gray-900">
                      {operationLabels[payment.operation] || operationLabels['default']}
                    </span>
                  </div>

                  <div className="md:w-1/4 mb-2 md:mb-0">
                    <span className="md:hidden text-xs font-bold text-gray-500">Částka: </span>
                    <AmountDisplay amount={payment.amount}/>
                  </div>

                  {activeTab === 'platby' && (
                    <div className="md:w-1/5 mb-2 md:mb-0">
                      <span className="md:hidden text-xs font-bold text-gray-500">Stav: </span>
                      <PaymentStatusBadge status={payment.status} />
                    </div>
                  )}
                  
                  {/* Action Column */}
                  <div className="md:w-1/4 mt-2 md:mt-0">
                    {activeTab === 'platby' ? (
                      <button
                        onClick={() => downloadInvoice(payment.id)}
                        className="inline-flex items-center justify-center p-1.5 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400"
                        title="Stáhnout daňový doklad"
                      >
                        <Download size={14} />
                      </button>
                    ) : (
                      payment.file_id && (
                        <button
                          onClick={() => navigate(`/analysis/${payment.file_id}/summary`)}
                          className="inline-flex items-center justify-center p-1.5 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400"
                          title="Přejít na detail analýzy"
                        >
                          <ArrowRight size={14} />
                        </button>
                      )
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {/* Pagination Footer */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-200">
                <div className="flex flex-1 justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Předchozí
                  </button>
                  <button
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Další
                  </button>
                </div>

                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Zobrazeno <span className="font-medium">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span> až <span className="font-medium">{Math.min(currentPage * ITEMS_PER_PAGE, filteredPayments.length)}</span> z <span className="font-medium">{filteredPayments.length}</span> výsledků
                    </p>
                  </div>
                  <div>
                    <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                      <button
                        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Previous</span>
                        <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                      </button>
                      
                      <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 focus:outline-offset-0">
                        {currentPage} / {totalPages}
                      </span>

                      <button
                        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Next</span>
                        <ChevronRight className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full h-full bg-gray-50">
      <div className="container mx-auto max-w-4xl py-12 px-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <h1 className="text-4xl font-extrabold text-gray-900">Historie transakcí</h1>
        </div>
        {renderContent()}
      </div>
    </div>
  );
};

export default PaymentHistoryPage;